// @ts-nocheck

import React, { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { X, User, Search } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { toast } from "sonner";

interface Profile {
  id: string;
  name: string;
  username: string;
  avatar_url?: string;
}

interface UserMentionSelectorProps {
  selectedUsers: Profile[];
  onUsersChange: (users: Profile[]) => void;
}

const UserMentionSelector: React.FC<UserMentionSelectorProps> = ({
  selectedUsers,
  onUsersChange
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<Profile[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  // Search for users when query changes
  useEffect(() => {
    const searchUsers = async () => {
      if (!searchQuery.trim() || searchQuery.length < 2) {
        setSearchResults([]);
        return;
      }

      try {
        setIsLoading(true);
        console.log('🔍 Searching for users:', searchQuery);

        const { data, error } = await supabase
          .from('profiles')
          .select('id, name, username, avatar_url')
          .or(
            `name.ilike.%${searchQuery}%,username.ilike.%${searchQuery}%`
          )
          .limit(10);

        if (error) {
          console.error('❌ Error searching users:', error);
          throw error;
        }

        console.log('✅ Found users:', data?.length || 0);
        
        // Filter out already selected users
        const filteredResults = (data || []).filter(
          user => !selectedUsers.some(selected => selected.id === user.id)
        );
        
        setSearchResults(filteredResults);
      } catch (error) {
        console.error('❌ Error searching users:', error);
        toast.error('Failed to search users');
      } finally {
        setIsLoading(false);
      }
    };

    const timeoutId = setTimeout(searchUsers, 300);
    return () => clearTimeout(timeoutId);
  }, [searchQuery, selectedUsers]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    console.log('🔤 User typing:', value); // Debug logging
    setSearchQuery(value);
    
    // Open popover when user starts typing
    if (value.trim().length > 0) {
      setIsOpen(true);
    }
  };

  const handleSelectUser = (user: Profile) => {
    console.log('👤 Selecting user:', user.name);
    onUsersChange([...selectedUsers, user]);
    setSearchQuery("");
    setSearchResults([]);
    setIsOpen(false);
  };

  const handleRemoveUser = (userId: string) => {
    console.log('❌ Removing user:', userId);
    onUsersChange(selectedUsers.filter(user => user.id !== userId));
  };

  const handleInputFocus = () => {
    console.log('🎯 Input focused');
    if (searchQuery.trim().length > 0) {
      setIsOpen(true);
    }
  };

  const handleInputBlur = () => {
    console.log('👋 Input blurred');
    // Delay closing to allow for item selection
    setTimeout(() => {
      setIsOpen(false);
    }, 200);
  };

  return (
    <div className="space-y-3">
      <Label>Mention Athletes</Label>
      <div className="space-y-2">
        {/* Search Input */}
        <div className="relative">
          <Input
            placeholder="Search athletes by name or username..."
            value={searchQuery}
            onChange={handleInputChange}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
            className="pl-10"
          />
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          
          {/* Search Results Dropdown */}
          {isOpen && (searchResults.length > 0 || isLoading) && (
            <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
              {isLoading ? (
                <div className="p-3 text-sm text-gray-500">Searching...</div>
              ) : searchResults.length > 0 ? (
                <div className="py-1">
                  {searchResults.map((user) => (
                    <div
                      key={user.id}
                      className="flex items-center gap-2 px-3 py-2 hover:bg-gray-100 cursor-pointer"
                      onClick={() => handleSelectUser(user)}
                    >
                      <Avatar className="h-6 w-6">
                        <AvatarImage src={user.avatar_url} />
                        <AvatarFallback>
                          <User className="h-3 w-3" />
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex flex-col">
                        <span className="font-medium text-sm">{user.name}</span>
                        <span className="text-xs text-gray-500">@{user.username}</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          )}
        </div>

        {/* Selected Users */}
        {selectedUsers.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {selectedUsers.map((user) => (
              <Badge
                key={user.id}
                variant="secondary"
                className="flex items-center gap-2 pr-1"
              >
                <Avatar className="h-4 w-4">
                  <AvatarImage src={user.avatar_url} />
                  <AvatarFallback>
                    <User className="h-2 w-2" />
                  </AvatarFallback>
                </Avatar>
                <span>{user.name}</span>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-4 w-4 p-0 hover:bg-transparent"
                  onClick={() => handleRemoveUser(user.id)}
                >
                  <X className="h-3 w-3" />
                </Button>
              </Badge>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default UserMentionSelector;